import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AppInitializerService } from '@app/app-initializer.service';
import { AdvancedSearchModel } from '@app/components/controls/utilities/models/advanced-search-model';
import { AutoCompleteModel } from '@app/components/controls/utilities/models/auto-complete-model';
import { CheckBoxModel } from '@app/components/controls/utilities/models/check-box-model';
import { DropDownModel } from '@app/components/controls/utilities/models/drop-down-model';
import { QuillEditorModel } from '@app/components/controls/utilities/models/quill-editor-model';
import { TextAreaModel } from '@app/components/controls/utilities/models/text-area-model';
import { TextBoxModel } from '@app/components/controls/utilities/models/text-box-model';
import { WeightageModel } from '@app/components/controls/utilities/models/weightage-model';
import { BuilderService } from '@app/components/controls/utilities/services/builder.service';
import { PopupComponent } from '@app/components/popup/popup.component';
import { OptionMenu } from '@app/layout/utilities/models/layout-model';
import { BaseService } from '@app/system/services/base.service';
import { DetectDeviceService } from '@app/system/services/detect-device.service';
import { GlobalApiService } from '@app/system/services/global-api.service';
import { GlobalFunctionService } from '@app/system/services/global-function.service';
import { LocaleService } from '@app/system/services/locale.service';
import { map } from 'rxjs/operators';
import { ApiService } from '../utilities/services/api.service';

@Component({
	selector: 'app-assessment-forms',
	templateUrl: './assessment-forms.component.html',
	styleUrls: ['./assessment-forms.component.scss']
})
export class AssessmentFormsComponent implements OnInit {

	@ViewChild('formElement') set setFocus(content: ElementRef) {
		if (this.action === 'questions' && content && this.detectDevice.resolutionState$.value.deviceType === 'desktop') {
			const el: HTMLElement = content.nativeElement.querySelector('#search');
			if (el) {
				const parentEl: HTMLElement = (el.firstChild as HTMLElement);
				parentEl.getElementsByTagName('input')[0].focus();
			}
		}
	}

	action: 'createTest' | 'questionDistribution' | 'questions' | 'updateTest' | 'addQuestion' | 'updateAssessment' | 'viewQuestion' | 'editQuestion';
	record;
	config = { fxFlex: 100 };
	formObject;
	skillFormObject;
	submitDisable = true;
	popupRef;
	title = '';
	currentUser;
	info = '';
	formFields: any = [];
	optionMenu: Array<OptionMenu> = [];
	distributedData = [];
	canAddDistribution = {
		mcq: false,
		descriptive: false
	};
	clearTimeout;
	skillLevelData = [];
	tabIndex = 0;
	questions = [];
	loader = false;
	questionCount = 0;
	limit = 10;
	pageNumber = 1;
	search: AdvancedSearchModel;
	form: UntypedFormGroup;
	totalQuestions = 0;
	totalPoints = 0;
	skillField: any = [];
	assessmentID;
	distributionLoader = false;
	optionsForm: UntypedFormArray;
	optionsField = [];
	optionOne: TextBoxModel;
	subscribes = [];
	coveredSkills = [];
	courseDetails;
	optionSample;
	constructor(
		public gfService: GlobalFunctionService,
		public locale: LocaleService,
		private apiService: ApiService,
		private popup: MatDialog,
		public detectDevice: DetectDeviceService,
		private globalApiService: GlobalApiService,
		private appInit: AppInitializerService,
		private builderService: BuilderService,
		private baseService: BaseService
	) {
		this.currentUser = this.gfService.sessionUser;
		if (this.currentUser.weightageData) {
			this.skillLevelData = this.currentUser.weightageData.map((data) => {
				return {
					key: data.termID,
					value: data.term,
					weightage: data.index,
					description: data.attributes.description
				};
			});
		}
	}

	ngOnInit(): void {
		if (!['questionDistribution', 'updateAssessment'].includes(this.action)) {
			this.baseService.formView = true;
		}
		if (this.action === 'createTest' || this.action === 'updateTest') {
			if (this.action === 'createTest') {
				this.title = this.locale.translate('create_test');
			} else {
				this.title = this.locale.translate('edit_test');
			}
			this.popupRef.componentInstance.popupLoader = true;
			const name: TextBoxModel = {
				fieldColumn: 'name',
				additionalMetaData: {
					maxChar: 250,
					fieldConfig: { appearance: 'standard' },
					showLabel: true
				},
				fieldType: 'textBox',
				isHidden: false,
				isReadonly: false,
				isRequired: true,
				type: 'text',
				validationRegex: null,
				fieldCaption: this.locale.translate('name'),
				fieldHelpText: null,
				fieldOrder: 1,
				validationMessage: '',
				fieldPlaceholder: this.locale.translate('name')
			};
			const description: TextAreaModel = {
				fieldColumn: 'description',
				additionalMetaData: {
					maxChar: 500,
					showLabel: true
				},
				fieldType: 'textArea',
				isHidden: false,
				isReadonly: false,
				isRequired: false,
				validationRegex: null,
				fieldCaption: this.locale.translate('desc'),
				fieldHelpText: null,
				fieldOrder: 2,
				validationMessage: '',
				fieldPlaceholder: this.locale.translate('desc')
			};

			const duration: TextBoxModel = {
				fieldColumn: 'duration',
				additionalMetaData: {
					max: 200,
					fieldConfig: { appearance: 'standard' },
					showLabel: true
				},
				fieldType: 'textBox',
				isHidden: false,
				isReadonly: false,
				isRequired: true,
				type: 'number',
				validationRegex: null,
				fieldCaption: this.locale.translate('duration_in_mins'),
				fieldHelpText: null,
				fieldOrder: 3,
				validationMessage: '',
				fieldPlaceholder: this.locale.translate('duration_in_mins')
			};
			const autoGenerateQuestion: CheckBoxModel = {
				fieldColumn: 'autogenerateQuestion',
				additionalMetaData: {
					showLabel: true
				},
				fieldType: 'checkBox',
				isHidden: false,
				isReadonly: false,
				isRequired: false,
				options: [{
					key: true,
					value: this.locale.translate('auto_select_questions_at_runtime')
				}],
				fieldCaption: null,
				fieldOrder: 4
			};
			if (this.action === 'updateTest' && this.assessmentID) {
				const apiData = {};
				const headers: any = {};
				this.apiService.getTestDetail(apiData, this.assessmentID, headers).subscribe(
					(response) => {
						if (response) {
							name.value = response.name;
							description.value = response.description;
							duration.value = response.duration;
							autoGenerateQuestion.value = [(response.autogenerateQuestion === 1) ? true : false];
						}
					},
					(error) => {
						this.gfService.errorHandler(error, {
							snackBar: {
								position: 'before'
							}
						});
					}
				)
					.add(() => {
						autoGenerateQuestion.isReadonly = true;
						this.formFields = [
							name,
							description,
							duration,
							autoGenerateQuestion
						];
						this.popupRef.componentInstance.popupLoader = false;
						this.submitDisable = false;
					});
			} else {
				this.formFields = [
					name,
					description,
					duration,
					autoGenerateQuestion
				];
				this.popupRef.componentInstance.popupLoader = false;
				this.submitDisable = false;
			}
		} else if (this.action === 'questionDistribution' || this.action === 'updateAssessment') {
			this.popupRef.componentInstance.popupLoader = true;
			const apiData = {};
			const headers: any = {};
			this.apiService.getTestDetail(apiData, this.assessmentID, headers).subscribe(
				(response) => {
					if (response) {
						this.title = response.name;
						this.record = {
							testData: response
						};
						if (this.popupRef) {
							this.popupRef.componentInstance.dialogCloseData = {
								testData: this.record.testData,
								totalPoints: this.totalPoints
							};
						}
						this.optionMenu = [
							{
								key: 'edit',
								caption: this.locale.translate('edit'),
								classes: [],
								disabled: false
							},
							{
								key: 'delete',
								caption: this.locale.translate('delete'),
								classes: [],
								disabled: false
							}
						];
						const sectionApiData = {
							testID: this.assessmentID
						};
						const sectionHeaders: any = {};
						this.distributionLoader = true;
						this.apiService.getSections(sectionApiData, sectionHeaders).subscribe(
							(sectionResponse) => {
								if (sectionResponse && sectionResponse.records.length) {
									this.dataBuild(sectionResponse.records, 'section');
									this.distributedData = sectionResponse.records;
									this.calculateTotal();
								}
							},
							(error) => {
								this.gfService.errorHandler(error, {
									snackBar: {
										position: 'before'
									}
								});
							}
						)
							.add(() => {
								this.distributionLoader = false;
							});
					}
				},
				(error) => {
					this.gfService.errorHandler(error, {
						snackBar: {
							position: 'before'
						}
					});
				}
			)
				.add(() => {
					this.popupRef.componentInstance.popupLoader = false;
					this.submitDisable = false;
				});
		} else if (this.action === 'questions') {
			this.title = this.locale.translate('select_questions');
			this.popupRef.componentInstance.popupLoader = true;
			this.search = {
				additionalMetaData: {
					filter: []
				},
				category: null,
				fieldColumn: 'search',
				fieldType: 'advancedSearch',
				isHidden: false,
				isReadonly: false,
				fieldCaption: '',
				fieldHelpText: '',
				fieldHelpTextLong: '',
				fieldOrder: 1,
				fieldPlaceholder: 'Search',
				isRequired: false,
				options: [],
				selectedValue: [],
				type: 'classic'
			};
			this.formFields = [this.search];
			const data = this.builderService.formGroupBuilder(this.formFields);
			this.form = data;
			this.loader = true;
			this.getQuestion();
			this.submitDisable = false;
			this.popupRef.componentInstance.popupLoader = false;
		} else if (this.action === 'addQuestion' || this.action === 'editQuestion') {
			if (this.action === 'addQuestion') {
				this.title = this.locale.translate('new_question');
			} else {
				this.title = this.locale.translate('edit_question');
			}
			this.popupRef.componentInstance.popupLoader = true;
			const question: QuillEditorModel = {
				fieldCaption: this.locale.translate('question'),
				fieldColumn: 'question',
				validationRegex: '',
				validationMessage: '',
				fieldOrder: 1,
				fieldType: 'quillEditor',
				fieldHelpText: '',
				isRequired: true,
				isHidden: false,
				additionalMetaData: {
					// height: 400,
					showLabel: true,
					imageUploadData: {
						fileSize: '10MB',
						supportedType: ['.jpg', '.jpeg', '.png', '.gif'],
						url: ''
					},
					config: {
						toolbar: [
							['link', 'image', 'video', 'formula', 'code-block']
						],
						// toolbar: '#toolbar-container',
						formula: true,
						imageResize: true
					}
				},
				isReadonly: false,
				value: null,
				fieldPlaceholder: this.locale.translate('type_question')
			};
			this.optionOne = {
				fieldColumn: '1',
				additionalMetaData: {
					maxChar: 250,
					fieldConfig: { appearance: 'standard' },
					showLabel: true
				},
				fieldType: 'textBox',
				isHidden: false,
				isReadonly: false,
				isRequired: false,
				type: 'text',
				validationRegex: null,
				fieldCaption: this.locale.translate('option_1'),
				fieldHelpText: null,
				fieldOrder: 1,
				validationMessage: '',
				fieldPlaceholder: this.locale.translate('option_1')
			};
			const rightOption: DropDownModel = {
				fieldColumn: 'rightOption',
				fieldType: 'dropDown',
				isHidden: false,
				isMultiple: false,
				isReadonly: false,
				isRequired: true,
				options: [],
				additionalMetaData: {},
				fieldCaption: this.locale.translate('right_answer'),
				fieldPlaceholder: this.locale.translate('right_answer'),
				value: null
			};
			const skills: AutoCompleteModel = {
				fieldColumn: 'skills',
				additionalMetaData: {
					showLabel: true,
					autoFill: 'off',
					defaultWeightage: 1,
					minWeightage: 1,
					showHelp: true,
					max: 1,
					maxWeightage: this.skillLevelData
				},
				fieldType: 'autoComplete',
				isHidden: false,
				isMultiple: true,
				isReadonly: true,
				isRequired: true,
				options: [],
				selectedValue: [],
				fieldCaption: this.locale.translate('skill_required_to_answer_this_question'),
				fieldOrder: 6,
				preDefined: true,
				fieldPlaceholder: this.locale.translate('skill_required_to_answer_this_question'),
				type: 'weightage'
			};
			if (this.record) {
				if (this.action === 'addQuestion') {
					skills.selectedValue = [
						{
							key: {
								key: this.record.skillID,
								value: this.record.skillName
							},
							value: this.record.skillName,
							isDelete: false,
							isAdd: true,
							weightage: this.record.skillLevel
						}
					];
				} else {
					skills.selectedValue = [
						{
							key: {
								key: this.record.skill.ID,
								value: this.record.skill.name
							},
							value: this.record.skill.name,
							isDelete: false,
							isAdd: true,
							weightage: this.record.skillLevel
						}
					];
				}
				skills.value = skills.selectedValue;
			}
			// this.loadMySkills(skills);
			this.formFields = [
				question,
				// optionOne,
				rightOption,
				skills
			];
			this.optionsField = [this.optionOne];
			// this.skillField = [skills];
			this.form = this.builderService.formGroupBuilder(this.formFields);
			this.skillFieldManage();
			this.form.addControl('options', new UntypedFormArray([]));
			this.optionsForm = this.form.get('options') as UntypedFormArray;
			const control = new UntypedFormControl(null, Validators.required);
			this.optionsForm.push(control);
			if (this.action === 'editQuestion' && this.record.questionID) {
				const apiData = {};
				const headers: any = {};
				this.apiService.getQuestionDetails(apiData, this.record.questionID, headers).subscribe(
					(response) => {
						if (response) {
							this.record = response;
							rightOption.value = response.details.rightOption;
							question.value = response.question;
						}
					},
					(error) => {
						this.gfService.errorHandler(error, {
							snackBar: {
								position: 'before'
							}
						});
					}
				)
					.add(() => {

						this.formFields = [
							question,
							// optionOne,
							rightOption,
							skills
						];

						this.form = this.builderService.formGroupBuilder(this.formFields);
						// options edit
						this.form.addControl('options', new UntypedFormArray([]));
						const options = this.form.get('options') as UntypedFormArray;
						if (this.record.details.options) {
							this.optionsField[0].isRequired = true;
							let index = 0;
							for (const data of this.record.details.options) {
								options.push(new UntypedFormControl(data?.option));
								const nextOption = { ...this.optionOne };
								nextOption.isRequired = true;
								if (index === (this.record.details.options.length - 1)) {
									nextOption.isRequired = false;
								}
								nextOption.fieldCaption = `${this.locale.translate('option')} ${index + 1}`;
								nextOption.fieldPlaceholder = `${this.locale.translate('option')} ${index + 1}`;
								nextOption.helpLine = [];
								control.markAsUntouched();
								this.optionsField.push(nextOption);
								index = index + 1;
							}
						}
						this.optionsField.shift();
						this.optionsForm = options;
						this.manageOptionField();
						rightOption.options = this.record.details.options.map((data) => {
							return {
								key: data.index,
								value: data.option
							};
						});
						this.popupRef.componentInstance.popupLoader = false;
					});
			} else {
				this.formFields = [
					question,
					// optionOne,
					rightOption,
					skills
				];
				this.popupRef.componentInstance.popupLoader = false;
			}


			this.popupRef.componentInstance.popupLoader = false;
			this.submitDisable = false;
			this.manageOptionField();

		} else if (this.action === 'viewQuestion') {
			this.title = this.locale.translate('question');
			this.popupRef.componentInstance.popupLoader = true;
			this.optionMenu = [
				{
					key: 'editQues',
					caption: this.locale.translate('edit'),
					classes: [],
					disabled: false
				},
				{
					key: 'deleteQues',
					caption: this.locale.translate('delete'),
					classes: [],
					disabled: false
				}
			];

			this.popupRef.componentInstance.popupLoader = false;
			this.submitDisable = false;

		}



		// let form: FormGroup;
		// form = new FormGroup(
		// 	{
		// 		options: new FormArray([])
		// 	}
		// );
		// optionsForm.addControl('options', arrayControl)
		// // optionsForm.addControl('options', arrayControl);
		// const records = {
		// 	options: [
		// 		'option 1',
		// 		'options 2',
		// 		'options 3'
		// 	]
		// };
		// for (const data of records.options) {
		// 	form.controls.options.push(new FormControl(data));
		// }
		// console.log(form);


	}
	skillFieldManage() {
		if (this.action === 'addQuestion' && this.form.controls.skills && navigator.onLine) {
			const skill = this.formFields.find((items) => items.fieldColumn === 'skills');
			let subscribe;
			this.form.controls.skills.valueChanges.subscribe((controlResp) => {
				if (navigator.onLine) {
					if (subscribe && !subscribe['closed']) {
						subscribe.unsubscribe();
					}
					if (this.clearTimeout) {
						clearTimeout(this.clearTimeout);
					}
					this.clearTimeout = setTimeout(() => {
						if (controlResp && typeof controlResp === 'string') {
							const apiData = {
								searchString: controlResp,
								termsetid: 1
							};
							const header: any = {};
							let selectedValue = [];
							if (skill.selectedValue.length) {
								skill.selectedValue.map(items => {
									if (items.isAdd && !items.isDelete) {
										selectedValue.push(items.key);
									}
								});
							}
							subscribe = this.globalApiService.getTerm(apiData, header).subscribe(
								(response) => {
									if (response && response.records.length) {
										if (selectedValue.length) {
											response.records = response.records.filter(items => {
												if (!selectedValue.includes(items.termID)) {
													return items;
												}
											});
										}
										skill.options = response.records.map((data) => {
											return {
												key: {
													key: data.termID,
													value: data.term
												},
												value: data.term
											};
										});
									} else {
										skill.options = [];
									}
								},
								(error) => {
									this.gfService.errorHandler(error, {
										snackBar: {
											position: 'before'
										}
									});
									skill.options = [];
								}
							);
						} else {
							if (typeof controlResp === 'object') {
								skill.options = [];
							} else {
								// this.loadMySkills(skill);
							}
						}
					}, this.appInit.configuration.searchDelay);
				}
			});
		}
	}

	manageOptionField() {
		this.optionsForm.controls.map((control, index) => {
			if (navigator.onLine) {
				let searchValue = control.value;
				let status = false;
				this.subscribes.push(control.valueChanges.subscribe(resp => {
					if (navigator.onLine) {
						if (resp) {
							resp = resp.trim();
						}
						if (resp === searchValue && !status) {
							status = true;
							if (resp) {
								const findData = this.formFields[1].options.find(items => items.key === index + 1);
								if (!findData) {
									this.formFields[1].options.push(
										{
											key: index + 1,
											value: resp
										}
									);
								} else {
									findData.value = resp;
									findData.key = index + 1;
								}
								if (this.optionsField.length === index + 1) {
									this.optionsForm.controls[index].setValidators(Validators.required);
									this.optionsForm.controls[index].updateValueAndValidity();
									this.optionsField[index].isRequired = true;
									this.optionsField[index].helpLine = [
										{
											type: 'icon',
											icon: 'close'
										}
									];
									const control = new UntypedFormControl(null);
									const nextOption = { ...this.optionOne };
									nextOption.isRequired = false;
									nextOption.fieldCaption = `${this.locale.translate('option')} ${index + 2}`;
									nextOption.fieldPlaceholder = `${this.locale.translate('option')} ${index + 2}`;
									nextOption.helpLine = [];
									control.markAsUntouched();
									this.optionsField.push(nextOption);
									this.optionsForm.push(control);
									if (this.subscribes.length) {
										for (const subscribe of this.subscribes) {
											subscribe.unsubscribe();
										}
									}
									this.manageOptionField();
								}
							}
						} else {
							if (!resp) {
								this.formFields[1].options = this.formFields[1].options.filter(items => items.key !== index + 1);
							}
							status = false;
						}
						searchValue = resp;
					}
				}));
			}
		});
	}

	helpLineEmit(event, index) {
		if (navigator.onLine) {
			this.optionsField.splice(index, 1);
			this.optionsForm.removeAt(index);
			if (this.subscribes.length) {
				for (const subscribe of this.subscribes) {
					subscribe.unsubscribe();
				}
			}
			this.manageOptionField();
			this.formFields[1].options = [];
			this.form.controls.rightOption.patchValue(null, { emitEvent: false })
			this.optionsField.map((resp, index) => {
				resp.fieldCaption = `${this.locale.translate('option')} ${index + 1}`;
				resp.fieldPlaceholder = `${this.locale.translate('option')} ${index + 1}`;
				if (this.optionsForm.at(index).value) {
					this.formFields[1].options.push(
						{
							key: index + 1,
							value: this.optionsForm.at(index).value
						}
					);
				}
			});
		}
	}

	optionInfo(action) {
		if (action.key === 'edit') {
			this.editTest();
		} else if (action.key === 'delete') {
			this.alertPopup('deleteAssessment', null);
		} else if (action.key === 'editQues') {
			this.editQuestions()
		} else if (action.key === 'deleteQues') {
			this.alertPopup('deleteQuestion', null);
		}
	}

	editTest() {
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			autoFocus: true,
			data: {
				action: 'dynamic',
				popup: true,
				component: AssessmentFormsComponent,
				componentData: [
					{
						variableName: 'action',
						value: 'updateTest'
					},
					{
						variableName: 'assessmentID',
						value: this.assessmentID
					}
				]
			},
			disableClose: true,
			maxWidth: '860px'
		});
		dialogRef.afterClosed().subscribe(async (result) => {
			if (result && typeof result === 'object') {
				this.record.testData = result;
				this.title = this.record.testData.name;
				if (this.popupRef) {
					this.popupRef.componentInstance.dialogCloseData = {
						testData: this.record.testData,
						distributedData: this.distributedData
					};
				}
			}
		});
	}

	addMcq() {
		if (navigator.onLine) {
			this.popupRef.componentInstance.popupLoader = true;
			this.canAddDistribution.mcq = true;
			this.canAddDistribution.descriptive = false;
			let formFields = [];
			formFields = this.getDistributionField();
			const skills: AutoCompleteModel = {
				fieldColumn: 'skills',
				additionalMetaData: {
					showLabel: true,
					autoFill: 'off',
					defaultWeightage: 1,
					minWeightage: 1,
					showHelp: true,
					max: 1,
					maxWeightage: this.skillLevelData
				},
				fieldType: 'autoComplete',
				isHidden: false,
				isMultiple: true,
				isReadonly: false,
				isRequired: true,
				options: [],
				selectedValue: [],
				fieldCaption: this.locale.translate('search_skills'),
				fieldOrder: 6,
				preDefined: true,
				fieldPlaceholder: this.locale.translate('search_skills'),
				type: 'weightage'
			};
			if (this.coveredSkills && this.coveredSkills.length) {
				for (const skill of this.coveredSkills) {
					skills.options.push(
						{
							key: {
								key: skill.skill.key,
								value: skill.skill.value
							},
							value: skill.skill.value
						}
					);
				}
			}
			// this.loadMySkills(skills);
			formFields.unshift(skills);
			this.formFields = formFields;
			this.popupRef.componentInstance.popupLoader = false;
			this.submitDisable = false;
		}
	}

	// loadMySkills(skills) {
	// 	const apiData = {};
	// 	const headers: any = {};
	// 	skills.options = [];
	// 	this.globalApiService.getMySkills(apiData, headers).subscribe(
	// 		(response) => {
	// 			if (response && Array.isArray(response.records)) {
	// 				skills.options = response.records.map(resp => {
	// 					return {
	// 						key: {
	// 							key: resp.skill,
	// 							value: resp.skillName
	// 						},
	// 						value: resp.skillName
	// 					};
	// 				});
	// 			}
	// 		},
	// 		(error) => {
	// 			this.gfService.errorHandler(error, {
	// 				snackBar: {
	// 					position: 'before'
	// 				}
	// 			});
	// 		}
	// 	)
	// 		.add(() => {
	// 		});
	// }

	formObjectReader(record: any) {
		this.formObject = record;
		const form: UntypedFormGroup = record.form;
		if ((this.action === 'questionDistribution' || this.action === 'updateAssessment') && form.controls.skills) {
			const skill = this.formFields.find((items) => items.fieldColumn === 'skills');
			form.controls.skills.valueChanges.subscribe((controlResp) => {
				if (navigator.onLine) {
					if (controlResp && typeof controlResp === 'string') {
						let selectedValue = [];
						if (skill.selectedValue.length) {
							skill.selectedValue.map(items => {
								if (items.isAdd && !items.isDelete) {
									selectedValue.push(items.key);
								}
							});
						}
						if (this.coveredSkills && this.coveredSkills.length) {
							this.coveredSkills = this.coveredSkills.filter(items => {
								if (!selectedValue.includes(items.skill.key)) {
									return items;
								}
							});
							skill.options = [];
							this.coveredSkills.map(option => {
								if (option.skill.value.toLowerCase().indexOf(controlResp.toLowerCase()) === 0) {
									skill.options.push(
										{
											key: {
												key: option.skill.key,
												value: option.skill.value
											},
											value: option.skill.value
										}
									);
								}
							});
						}
					} else {
						if (typeof controlResp === 'object') {
							skill.options = [];
						} else {
							if (this.coveredSkills && this.coveredSkills.length) {
								skill.options = [];
								for (const data of this.coveredSkills) {
									skill.options.push(
										{
											key: {
												key: data.skill.key,
												value: data.skill.value
											},
											value: data.skill.value
										}
									);
								}
							}
							// this.loadMySkills(skill);
						}
					}
				}
			});
		}
	}

	editFormObjectReader(record: any, data) {
		data.formObject = record;
		const form: UntypedFormGroup = record.form;
		if (form.controls.skills) {
			const skill = data.formObject.fields.find((items) => items.fieldColumn === 'skills');
			form.controls.skills.valueChanges.subscribe((controlResp) => {
				if (navigator.onLine) {
					if (controlResp && typeof controlResp === 'object') {
						data.formFields[0].isHidden = false;
						data.formFields[0].isRequired = true;
						data.formFields[0].value = null;
						data.formFields[0].fieldCaption = controlResp.value;
						data.skillID = controlResp.key.key;

						data.formFields[1].isHidden = true;
						data.formFields[1].isRequired = false;
					}
					if (controlResp && typeof controlResp === 'string') {
						let selectedValue = [];
						if (skill.selectedValue.length) {
							skill.selectedValue.map(items => {
								if (items.isAdd && !items.isDelete) {
									selectedValue.push(items.key);
								}
							});
						}
						if (this.coveredSkills && this.coveredSkills.length) {
							this.coveredSkills = this.coveredSkills.filter(items => {
								if (!selectedValue.includes(items.skill.key)) {
									return items;
								}
							});
							skill.options = [];
							this.coveredSkills.map(option => {
								if (option.skill.value.toLowerCase().indexOf(controlResp.toLowerCase()) === 0) {
									skill.options.push(
										{
											key: {
												key: option.skill.key,
												value: option.skill.value
											},
											value: option.skill.value
										}
									);
								}
							});
						}
					} else {
						if (typeof controlResp === 'object') {
							skill.options = [];
						} else {
							if (this.coveredSkills && this.coveredSkills.length) {
								skill.options = [];
								for (const data of this.coveredSkills) {
									skill.options.push(
										{
											key: {
												key: data.skill.key,
												value: data.skill.value
											},
											value: data.skill.value
										}
									);
								}
							}
							// this.loadMySkills(skill);
						}
					}
				}
			});
		}
	}

	skillFormReader(record: any) {
		this.skillFormObject = record;
		const form: UntypedFormGroup = record.form;
		if (this.action === 'addQuestion' && form.controls.skills) {
			const skill = this.skillField.find((items) => items.fieldColumn === 'skills');
			let subscribe;
			form.controls.skills.valueChanges.subscribe((controlResp) => {
				if (navigator.onLine) {
					if (subscribe && !subscribe['closed']) {
						subscribe.unsubscribe();
					}
					if (this.clearTimeout) {
						clearTimeout(this.clearTimeout);
					}
					this.clearTimeout = setTimeout(() => {
						if (controlResp && typeof controlResp === 'string') {
							const apiData = {
								searchString: controlResp,
								termsetid: 1
							};
							const header: any = {};
							let selectedValue = [];
							if (skill.selectedValue.length) {
								skill.selectedValue.map(items => {
									if (items.isAdd && !items.isDelete) {
										selectedValue.push(items.key);
									}
								});
							}
							subscribe = this.globalApiService.getTerm(apiData, header).subscribe(
								(response) => {
									if (response && response.records.length) {
										if (selectedValue.length) {
											response.records = response.records.filter(items => {
												if (!selectedValue.includes(items.termID)) {
													return items;
												}
											});
										}
										skill.options = response.records.map((data) => {
											return {
												key: {
													key: data.termID,
													value: data.term
												},
												value: data.term
											};
										});
									} else {
										skill.options = [];
									}
								},
								(error) => {
									this.gfService.errorHandler(error, {
										snackBar: {
											position: 'before'
										}
									});
									skill.options = [];
								}
							);
						} else {
							if (typeof controlResp === 'object') {
								skill.options = [];
							} else {
								// this.loadMySkills(skill);
							}
						}
					}, this.appInit.configuration.searchDelay);
				}
			});
		}
	}

	getDistributionField() {
		const points: TextBoxModel = {
			fieldColumn: 'points',
			additionalMetaData: {
				max: 100,
				fieldConfig: { appearance: 'standard' },
				showLabel: true
			},
			fieldType: 'textBox',
			isHidden: false,
			isReadonly: false,
			isRequired: true,
			type: 'number',
			validationRegex: null,
			fieldCaption: this.locale.translate('points_per_question'),
			fieldHelpText: null,
			fieldOrder: 1,
			validationMessage: '',
			fieldPlaceholder: this.locale.translate('points_per_question')
		};
		const noOfQuestion: TextBoxModel = {
			fieldColumn: 'noOfQuestion',
			additionalMetaData: {
				max: 999,
				fieldConfig: { appearance: 'standard' },
				showLabel: true
			},
			fieldType: 'textBox',
			isHidden: false,
			isReadonly: false,
			isRequired: true,
			type: 'number',
			validationRegex: null,
			fieldCaption: this.locale.translate('no_of_questions'),
			fieldHelpText: null,
			fieldOrder: 1,
			validationMessage: '',
			fieldPlaceholder: this.locale.translate('no_of_questions')
		};
		return [
			points,
			noOfQuestion
		];
	}

	edit(data) {
		if (navigator.onLine) {
			data.action = 'editing';
			const formFields: any = this.getDistributionField();
			const weightage: WeightageModel = {
				fieldCaption: data.skillName,
				fieldColumn: 'weightAgeSkill',
				additionalMetaData: {
					maxWeightage: this.skillLevelData,
					minWeightage: 1,
					showHelp: true,
					removable: true,
					defaultWeightage: 1
				},
				fieldType: 'weightage',
				isHidden: false,
				isReadonly: false,
				isRequired: false,
				value: data.skillLevel
			};
			const skills: AutoCompleteModel = {
				fieldColumn: 'skills',
				additionalMetaData: {
					showLabel: true,
					autoFill: 'off',
					max: 1
				},
				fieldType: 'autoComplete',
				isHidden: true,
				isMultiple: true,
				isReadonly: false,
				isRequired: false,
				options: [],
				selectedValue: [],
				fieldCaption: this.locale.translate('search_skills'),
				fieldOrder: 1,
				preDefined: true,
				fieldPlaceholder: this.locale.translate('search_skills'),
				type: 'general'
			};
			if (this.coveredSkills && this.coveredSkills.length) {
				for (const skill of this.coveredSkills) {
					skills.options.push(
						{
							key: {
								key: skill.skill.key,
								value: skill.skill.value
							},
							value: skill.skill.value
						}
					);
				}
			}
			formFields.unshift(skills);
			formFields.unshift(weightage);
			formFields[2].value = data.points;
			formFields[3].value = data.noOfQuestion;
			data.formFields = formFields;
			this.submitDisable = false;
		}
	}

	removed(event, data) {
		if (navigator.onLine) {
			// data.formObject.fields.splice(0, 1);
			data.formObject.fields[0].isHidden = true;
			data.formObject.fields[0].isRequired = false;

			data.formObject.fields[1].isHidden = false;
			data.formObject.fields[1].isRequired = true;
			data.formObject.form.controls.skills.enable({ emitEvent: false });
			data.formObject.form.controls.skills.patchValue(null, { emitEvent: false });
			data.formObject.fields[1].value = null;
			data.formObject.fields[1].selectedValue = [];
		}
	}

	showQuestions(data) {
		let tabIndex = 0;
		if (!data.questions.length) {
			tabIndex = 1;
		}
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			data: {
				action: 'dynamic',
				popup: true,
				component: AssessmentFormsComponent,
				componentData: [
					{
						variableName: 'action',
						value: 'questions'
					},
					{
						variableName: 'record',
						value: data
					},
					{
						variableName: 'tabIndex',
						value: tabIndex
					}
				]
			},
			disableClose: true,
			maxWidth: '860px',
			autoFocus: false
		});
		dialogRef.afterClosed().subscribe((result) => {
			this.calculateTotal();
		});
	}

	newQuestion() {
		// this.popupRef.close();
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			data: {
				action: 'dynamic',
				popup: true,
				component: AssessmentFormsComponent,
				componentData: [
					{
						variableName: 'action',
						value: 'addQuestion'
					},
					{
						variableName: 'record',
						value: this.record
					}
				]
			},
			disableClose: true,
			maxWidth: '860px',
			autoFocus: false
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result && typeof result === 'object') {
				let status = false;
				if (this.tabIndex === 1) {
					status = true;
				}
				this.addQuestion({ ID: result.recordID }, status);
			}
		});
	}

	tabChange(index) {
		this.tabIndex = index;
		this.questionCount = 0;
		this.questions = [];
		this.loader = true;
		this.getQuestion(1);
	}

	getQuestion(pageNumber = 1, clearArr = false) {
		const headers = {};
		let apiData: any = {};
		let method;
		if (this.tabIndex === 0) {
			apiData = {
				limit: this.limit,
				pageNumber: pageNumber
			};
			if (this.formObject && this.formObject.form.controls.search.value) {
				apiData.searchString = this.formObject.form.controls.search.value;
			}
			method = this.apiService.getQuestionSection(apiData, this.record.recordID, headers);
		} else if (this.tabIndex === 1) {
			let selectedQuestions = [];
			if (this.record.questions.length) {
				for (const data of this.record.questions) {
					if (typeof data === 'number') {
						selectedQuestions.push(data);
					} else {
						selectedQuestions.push(data.ID);
					}
				}
			}
			apiData = {
				limit: this.limit,
				pageNumber: pageNumber,
				status: 1,
				skillID: this.record.skillID,
				questionType: this.record.questionTypeID,
				skillLevel: this.record.skillLevel
			};
			if (selectedQuestions.length) {
				apiData.excludeList = selectedQuestions.toString();
			}
			if (this.formObject && this.formObject.form.controls.search.value) {
				apiData.searchString = this.formObject.form.controls.search.value;
			}
			method = this.apiService.getQuestions(apiData, headers);
		}
		method.subscribe(
			(response) => {
				if (clearArr) {
					this.questions = [];
				}
				if (response && response.records.length) {
					this.questionCount = response.totalRecordCount;
					this.dataBuild(response.records, 'question');
				}
			},
			(error) => {
				this.gfService.errorHandler(error, {
					snackBar: {
						position: 'before'
					}
				});
				this.questionCount = 0;
				this.questions = [];
			}
		)
			.add(() => {
				this.loader = false;
			});
	}

	dataBuild(records, action: 'question' | 'section') {
		if (action === 'question') {
			for (const data of records) {
				data.ID = data.questionID;
				if (this.tabIndex === 0) {
					data.action = 'remove';
				} else if (this.tabIndex === 1) {
					data.action = 'select';
				}
				this.questions.push(data);
			}
		} else if (action === 'section') {
			for (const data of records) {
				data.recordID = data.sectionID;
				data.action = '';
				data.loader = false;
				data.skillID = data.skill.ID;
				data.skillName = data.skill.name;
				if (data.questionType) {
					const type = this.baseService.referenceType('questionType', data.questionType);
					data.type = type.caption;
					data.questionTypeID = type.value;
				}
			}
		}
	}

	searched() {
		if (navigator.onLine) {
			this.questionCount = 0;
			this.loader = true;
			this.getQuestion(1, true);
		}
	}

	clear() {
		if (navigator.onLine) {
			this.questionCount = 0;
			this.loader = true;
			this.getQuestion(1, true);
		}
	}

	addQuestion(data, tabChange = false) {
		const apiData: any = {};
		let questions = [...this.record.questions];
		questions.push(data.ID);
		apiData.questions = questions;
		const headers: any = {};
		this.popupRef.componentInstance.popupLoader = true;
		this.apiService.updateSection(apiData, this.record.recordID, headers).subscribe(
			(response) => {
				this.record.questions.push(data.ID);
				if (tabChange) {
					this.tabIndex = 0;
				} else {
					this.questionCount = 0;
					this.questions = [];
					this.loader = true;
					this.getQuestion(1);
				}
				// this.questions.splice(index, 1);
				// this.questionCount -= 1;
				this.gfService.openSnackBar('SUCCESS', {
					message: this.locale.translate('question_added'),
					icon: 'check_circle_outline',
					position: 'before'
				});
			},
			(error) => {
				this.gfService.errorHandler(error, {
					snackBar: {
						position: 'before'
					}
				});
			}
		)
			.add(() => {
				this.popupRef.componentInstance.popupLoader = false;
			});
	}

	removeQuestion(data) {
		if (navigator.onLine) {
			const apiData: any = {};
			let questions = [];
			for (const question of this.record.questions) {
				if (question !== data.ID) {
					questions.push(question);
				}
			}
			apiData.questions = questions;
			const headers: any = {};
			this.popupRef.componentInstance.popupLoader = true;
			this.apiService.updateSection(apiData, this.record.recordID, headers).subscribe(
				(response) => {
					// this.questions.splice(index, 1);
					// this.questionCount -= 1;
					this.questionCount = 0;
					this.questions = [];
					this.loader = true;
					this.getQuestion(1);
					this.record.questions = this.record.questions.filter(items => items !== data.ID);
					this.gfService.openSnackBar('SUCCESS', {
						message: this.locale.translate('question_removed'),
						icon: 'check_circle_outline',
						position: 'before'
					});
				},
				(error) => {
					this.gfService.errorHandler(error, {
						snackBar: {
							position: 'before'
						}
					});
				}
			)
				.add(() => {
					this.popupRef.componentInstance.popupLoader = false;
				});
		}
	}

	submit() {
		if (navigator.onLine) {
			if (this.action === 'createTest') {
				this.createTest();
			} else if (this.action === 'questionDistribution' || this.action === 'updateAssessment') {
				this.createDistribution();
			} else if (this.action === 'updateTest') {
				this.updateTest();
			} else if (this.action === 'addQuestion') {
				this.createQuestion();
			} else if (this.action === 'editQuestion') {
				this.updateQuestions();
			}
		}
	}

	alertPopup(action, data, additionalInfo: any = {}) {
		let title = '';
		let content = '';
		if (action === 'deleteDistribution') {
			title = this.locale.translate('delete');
			content = this.locale.translate('delete_question_distribution_text');
		} else if (action === 'deleteAssessment') {
			title = this.locale.translate('delete');
			content = this.locale.translate('delete_assessment_text');
		} else if (action === 'deleteQuestion') {
			title = this.locale.translate('delete');
			content = this.locale.translate('delete_question_text');
		}
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			autoFocus: false,
			data: {
				action: 'alert',
				classes: 'warning',
				popup: true,
				icon: 'warning',
				title,
				content,
				buttons: [
					{
						type: 'main-action',
						caption: this.locale.translate('yes'),
						key: 'yes'
					},
					{
						type: 'main-action',
						caption: this.locale.translate('no'),
						key: 'no',
						autoFocus: true
					}
				],
				triggerEl: additionalInfo.actionElement
			},
			disableClose: true,
			maxWidth: '560px'
		});
		dialogRef.afterClosed().subscribe((resp) => {
			if (resp) {
				if (resp.key === 'yes') {
					if (action === 'deleteDistribution') {
						this.deleteDistribution(data, additionalInfo);
					} else if (action === 'deleteAssessment') {
						this.deleteAssessment();
					} else if (action === 'deleteQuestion') {
						this.deleteQuestion();
					}
				}
			}
		});
	}

	deleteAssessment() {
		const headers: any = {};
		this.popupRef.componentInstance.popupLoader = true;
		let apiData = {};
		if (this.courseDetails && this.courseDetails.resource) {
			apiData = {
				courseID: this.courseDetails.resource.resourceID,
				moduleID: this.courseDetails.resource.infoFields.courseModules[this.courseDetails.moduleIndex].moduleID
			};
		}


		this.apiService.deleteTest(apiData, this.assessmentID, headers).subscribe(
			(response) => {
				this.popupRef.close({ action: 'deleted' });
				this.gfService.openSnackBar('SUCCESS', {
					message: this.locale.translate('test_deleted'),
					icon: 'check_circle_outline',
					position: 'before'
				});
			},
			(error) => {
				this.gfService.errorHandler(error, {
					snackBar: {
						position: 'before'
					}
				});
			}
		).add(() => {
			this.popupRef.componentInstance.popupLoader = false;
		});
	}

	deleteDistribution(data, additionalInfo) {
		const headers: any = {};
		data.loader = true;
		this.apiService.deleteSection(data.recordID, headers).subscribe(
			(response) => {
				this.distributedData.splice(additionalInfo.index, 1);
				data.loader = false;
				this.calculateTotal();
				this.gfService.openSnackBar('SUCCESS', {
					message: this.locale.translate('section_deleted'),
					icon: 'check_circle_outline',
					position: 'before'
				});
			},
			(error) => {
				this.gfService.errorHandler(error, {
					snackBar: {
						position: 'before'
					}
				});
				data.loader = false;
			}
		);
	}

	createTest() {
		if (this.formObject && this.formObject.form) {
			this.submitDisable = true;
			const headers: any = {};
			this.gfService.alertClear('assessment-alert');
			if (this.formObject.form.valid) {
				this.popupRef.componentInstance.popupLoader = true;
				const apiData: any = this.gfService.fieldSubmitApiValueConstruct(
					this.formObject.form.getRawValue(),
					this.formObject.fields
				);
				if (Array.isArray(apiData.autogenerateQuestion)) {
					if (apiData.autogenerateQuestion[0] !== null && apiData.autogenerateQuestion[0]) {
						apiData.autogenerateQuestion = 1;
					} else {
						apiData.autogenerateQuestion = 0;
					}
				}
				if (this.record && this.record.moduleID && this.record.resourceID) {
					apiData.moduleID = this.record.moduleID;
					apiData.courseID = this.record.resourceID;
				}
				this.apiService
					.createTest(apiData, headers)
					.subscribe(
						(response) => {
							const closeData = this.gfService.JSONMerge(apiData, response);
							this.popupRef.close(closeData);
							this.gfService.openSnackBar('SUCCESS', {
								message: this.locale.translate('test_added'),
								icon: 'check_circle_outline',
								position: 'before'
							});
						},
						(error) => {
							this.gfService.errorHandler(error, {
								alert: {
									id: 'assessment-alert'
								},
								form: this.formObject.form
							});
						}
					)
					.add(() => {
						this.submitDisable = false;
						this.popupRef.componentInstance.popupLoader = false;
					});
			} else {
				this.gfService.fieldControlMarkAsTouched(this.formObject.fields, this.formObject.form);
				this.submitDisable = false;
			}
		}
	}

	updateTest() {
		if (this.formObject && this.formObject.form) {
			this.submitDisable = true;
			const headers: any = {};
			this.gfService.alertClear('assessment-alert');
			if (this.formObject.form.valid) {
				this.popupRef.componentInstance.popupLoader = true;
				const apiData: any = this.gfService.fieldSubmitApiValueConstruct(
					this.formObject.form.getRawValue(),
					this.formObject.fields
				);
				if (Array.isArray(apiData.autogenerateQuestion)) {
					if (apiData.autogenerateQuestion[0] !== null && apiData.autogenerateQuestion[0]) {
						apiData.autogenerateQuestion = 1;
					} else {
						apiData.autogenerateQuestion = 0;
					}
				}
				this.apiService
					.updateTest(apiData, this.assessmentID, headers)
					.subscribe(
						(response) => {
							apiData.testID = this.assessmentID;
							const closeData = this.gfService.JSONMerge(apiData, response);
							this.popupRef.close(closeData);
							this.gfService.openSnackBar('SUCCESS', {
								message: this.locale.translate('test_updated'),
								icon: 'check_circle_outline',
								position: 'before'
							});
							if (apiData.autogenerateQuestion && this.totalQuestions) {
								for (const data of this.distributedData) {
									data.questions = [];
								}
								this.totalQuestions = 0;
							}
						},
						(error) => {
							this.gfService.errorHandler(error, {
								alert: {
									id: 'assessment-alert'
								},
								form: this.formObject.form
							});
						}
					)
					.add(() => {
						this.submitDisable = false;
						this.popupRef.componentInstance.popupLoader = false;
					});
			} else {
				this.gfService.fieldControlMarkAsTouched(this.formObject.fields, this.formObject.form);
				this.submitDisable = false;
			}
		}
	}

	createDistribution() {
		if (this.formObject && this.formObject.form) {
			this.submitDisable = true;
			const headers: any = {};
			this.gfService.alertClear('distribution-add-alert');
			if (this.formObject.form.valid) {
				this.loader = true;
				const apiData: any = this.gfService.fieldSubmitApiValueConstruct(
					this.formObject.form.getRawValue(),
					this.formObject.fields
				);
				const skills = [];
				const actualSKills = [];
				if (apiData.skills) {
					for (const skill of apiData.skills) {
						if (skill.isAdd && !skill.isDelete) {
							actualSKills.push(skill);
							skills.push({
								skill: skill.key.key,
								skillLevel: skill.progress
							});
						}
					}
				}
				apiData.skillID = skills[0].skill;
				apiData.skillLevel = skills[0].skillLevel;
				if (this.canAddDistribution.mcq) {
					apiData.questionType = this.baseService.questionType.find(items => items.key === 'mcq').value;
				} else if (this.canAddDistribution.descriptive) {

				}
				delete apiData.skills;
				this.apiService.createSection(apiData, this.assessmentID, headers).subscribe(
					(response) => {
						apiData.skills = actualSKills;
						apiData.skillName = actualSKills[0].key.value;
						this.processData(apiData, response);
						this.canAddDistribution.descriptive = false;
						this.canAddDistribution.mcq = false;
						this.gfService.openSnackBar('SUCCESS', {
							message: this.locale.translate('section_added'),
							icon: 'check_circle_outline',
							position: 'before'
						});
					},
					(error) => {
						this.gfService.errorHandler(error, {
							alert: {
								id: 'distribution-add-alert'
							},
							form: this.formObject.form
						});
					}
				)
					.add(() => {
						this.submitDisable = false;
						this.loader = false;
					});
			} else {
				this.gfService.fieldControlMarkAsTouched(this.formObject.fields, this.formObject.form);
				this.submitDisable = false;
			}
		}
	}

	updateDistribution(data) {
		if (navigator.onLine) {
			if (data.formObject && data.formObject.form) {
				this.submitDisable = true;
				const headers: any = {};
				this.gfService.alertClear('distribution-update-alert');
				if (data.formObject.form.valid) {
					data.loader = true;
					const formData: any = this.gfService.fieldSubmitApiValueConstruct(
						data.formObject.form.getRawValue(),
						data.formObject.fields
					);
					const apiData = {
						skillID: data.skillID,
						skillLevel: formData.weightAgeSkill,
						points: formData.points,
						noOfQuestion: formData.noOfQuestion
					};
					this.apiService.updateSection(apiData, data.recordID, headers).subscribe(
						(response) => {
							data.points = apiData.points;
							data.noOfQuestion = apiData.noOfQuestion;
							data.skillName = data.formObject.fields.find(items => items.fieldColumn === 'weightAgeSkill').fieldCaption;
							data.skillLevel = apiData.skillLevel;
							data.loader = false;
							data.action = null;
							this.calculateTotal();
							this.gfService.openSnackBar('SUCCESS', {
								message: this.locale.translate('section_updated'),
								icon: 'check_circle_outline',
								position: 'before'
							});
						},
						(error) => {
							this.gfService.errorHandler(error, {
								alert: {
									id: 'distribution-update-alert'
								},
								form: data.formObject.form
							});
						}
					)
						.add(() => {
							this.submitDisable = false;
							data.loader = false;
						});
				} else {
					this.gfService.fieldControlMarkAsTouched(data.formObject.fields, data.formObject.form);
					this.submitDisable = false;
				}
			}
		}
	}

	updateSkillLevel(data, percentage) {
		if (navigator.onLine) {
			const apiData = {
				skillLevel: percentage
			};
			const headers: any = {};
			this.apiService.updateSection(apiData, data.recordID, headers).subscribe(
				(response) => {

				},
				(error) => {
					this.gfService.errorHandler(error, {
						snackBar: {
							position: 'before'
						}
					});
				}
			)
				.add(() => { });
		}
	}

	createQuestion() {
		if (this.form) {
			this.submitDisable = true;
			const headers: any = {};
			this.gfService.alertClear('assessment-alert');
			if (this.form.valid) {
				this.popupRef.componentInstance.popupLoader = true;
				const formData: any = this.gfService.fieldSubmitApiValueConstruct(
					this.form.getRawValue(),
					this.formFields
				);
				const skills = [];
				if (formData.skills) {
					for (const skill of formData.skills) {
						if (skill.isAdd && !skill.isDelete) {
							skills.push({
								skill: skill.key.key,
								skillLevel: skill.progress
							});
						}
					}
				}
				const options = [];
				if (formData.options && formData.options.length) {
					formData.options.map((option, index) => {
						if (option) {
							options.push(
								{
									index: index + 1,
									option
								}
							);
						}
					});
				}
				formData.skills = skills;
				const apiData = {
					questionType: this.baseService.questionType.find(items => items.key === 'mcq').value,
					question: formData.question,
					details: {
						options,
						rightOption: formData.rightOption
					},
					skillID: formData.skills[0].skill,
					skillLevel: formData.skills[0].skillLevel,
					status: 1
				};
				this.apiService
					.addQuestion(apiData, headers)
					.subscribe(
						(response) => {
							const closeData = this.gfService.JSONMerge(formData, response);
							this.popupRef.close(closeData);
							this.gfService.openSnackBar('SUCCESS', {
								message: this.locale.translate('question_added'),
								icon: 'check_circle_outline',
								position: 'before'
							});
						},
						(error) => {
							this.gfService.alertHandler(error, 'assessment-alert', 'ERROR');
							this.gfService.formErrorHandler(error, this.form);
						}
					)
					.add(() => {
						this.submitDisable = false;
						this.popupRef.componentInstance.popupLoader = false;
					});
			} else {
				this.gfService.fieldControlMarkAsTouched(this.formFields, this.form);
				this.optionsField.map((option, index) => {
					if (option.touched === false && option.status === 'INVALID') {
						option.markAsTouched();
					}
				});
				this.submitDisable = false;
			}
		}
	}

	processData(apiData, response) {
		const type = this.baseService.questionType.find(items => items.key === 'mcq');
		const pushJson = this.gfService.JSONMerge(apiData, {
			type: type.caption,
			recordID: response.recordID,
			action: '',
			loader: false,
			questions: [],
			questionTypeID: type.value
		});
		this.distributedData.push(pushJson);
		// control.valueChanges.subscribe(resp => {
		// 	this.updateSkillLevel(pushJson, resp);
		// });
		this.calculateTotal();
	}

	calculateTotal() {
		this.totalPoints = 0;
		this.totalQuestions = 0;
		if (this.distributedData.length) {
			for (const data of this.distributedData) {
				this.totalPoints = this.totalPoints + (Number(data.points) * Number(data.noOfQuestion));
				this.totalQuestions = this.totalQuestions + Number(data.noOfQuestion);
			}
		}
		if (this.popupRef) {
			this.popupRef.componentInstance.dialogCloseData = {
				testData: this.record.testData,
				totalPoints: this.totalPoints
			};
		}
	}

	viewQuestions(data) {
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			data: {
				action: 'dynamic',
				popup: true,
				component: AssessmentFormsComponent,
				componentData: [
					{
						variableName: 'action',
						value: 'viewQuestion'
					},
					{
						variableName: 'record',
						value: data
					}
				]
			},
			disableClose: true,
			maxWidth: '860px',
			autoFocus: false
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result && typeof result === 'object') {
				if (result?.action === 'deleted') {
					this.questionCount = 0;
					this.questions = [];
					this.loader = true;
					this.getQuestion(1);
				} else {
					data.details.options = [];
					data.question = result.question;
					let index = 1;
					for (const option of result.details.options) {
						data.details.options.push(
							{
								index,
								option
							}
						);
					}
				}
			}
		});
	}

	editQuestions() {
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			data: {
				action: 'dynamic',
				popup: true,
				component: AssessmentFormsComponent,
				componentData: [
					{
						variableName: 'action',
						value: 'editQuestion'
					},
					{
						variableName: 'record',
						value: this.record
					}
				]
			},
			disableClose: true,
			maxWidth: '860px',
			autoFocus: false
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result && typeof result === 'object') {
				this.record.edited = true;
				this.record.details.options = [];
				this.record.question = result.question;
				let index = 1;
				for (const option of result.options) {
					this.record.details.options.push(
						{
							index,
							option
						}
					);
				}
			}
		});
	}

	viewQuestionCloseBtn() {
		if (this.record?.edited) {
			this.popupRef.close(this.record);
		} else {
			this.popupRef.close('cancel');
		}
	}

	updateQuestions() {
		if (this.form) {
			this.submitDisable = true;
			const headers: any = {};
			this.gfService.alertClear('assessment-alert');
			if (this.form.valid) {
				this.popupRef.componentInstance.popupLoader = true;
				const formData: any = this.gfService.fieldSubmitApiValueConstruct(
					this.form.getRawValue(),
					this.formFields
				);
				const skills = [];
				if (formData.skills) {
					for (const skill of formData.skills) {
						if (skill.isAdd && !skill.isDelete) {
							skills.push({
								skill: skill.key.key,
								skillLevel: skill.progress
							});
						}
					}
				}
				const options = [];
				if (formData.options && formData.options.length) {
					formData.options.map((option, index) => {
						if (option) {
							options.push(
								{
									index: index + 1,
									option
								}
							);
						}
					});
				}
				formData.skills = skills;
				const apiData = {
					questionType: this.baseService.questionType.find(items => items.key === 'mcq').value,
					question: formData.question,
					details: {
						options,
						rightOption: formData.rightOption
					},
					skillID: formData.skills[0].skill,
					skillLevel: formData.skills[0].skillLevel,
					status: 1
				};
				this.apiService
					.updateQuestionDetails(apiData, this.record.questionID, headers)
					.subscribe(
						(response) => {
							const closeData = this.gfService.JSONMerge(formData, response);
							this.popupRef.close(closeData);
							this.gfService.openSnackBar('SUCCESS', {
								message: this.locale.translate('question_updated'),
								icon: 'check_circle_outline',
								position: 'before'
							});
						},
						(error) => {
							this.gfService.alertHandler(error, 'assessment-alert', 'ERROR');
							this.gfService.formErrorHandler(error, this.form);
						}
					)
					.add(() => {
						this.submitDisable = false;
						this.popupRef.componentInstance.popupLoader = false;
					});
			} else {
				this.gfService.fieldControlMarkAsTouched(this.formFields, this.form);
				this.optionsField.map((option, index) => {
					if (option.touched === false && option.status === 'INVALID') {
						option.markAsTouched();
					}
				});
				this.submitDisable = false;
			}
		}
	}

	deleteQuestion() {
		const headers: any = {};
		this.popupRef.componentInstance.popupLoader = true;
		let apiData = {
			resourceID: this.record.resourceID
		};
		this.apiService.deleteQuestion(this.record.questionID, headers).subscribe(
			(response) => {
				this.popupRef.close({ action: 'deleted' });
				this.gfService.openSnackBar('SUCCESS', {
					message: this.locale.translate('question_deleted'),
					icon: 'check_circle_outline',
					position: 'before'
				});
			},
			(error) => {
				this.gfService.errorHandler(error, {
					snackBar: {
						position: 'before'
					}
				});
			}
		).add(() => {
			// this.getQuestion();
			// this.getQuestion(1, true);
			this.popupRef.componentInstance.popupLoader = false;
		});
	}
}
